/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { APPSTATE, MAILSTATE } from '.'
import {
    Box,
    IMailMessage,
    MailBody,
    MailboxPayload,
    MailState,
} from '../../types/state'
import {
    API_MAIL_getMessage,
    API_MAIL_getMessages,
    API_MAIL_getSentMessages,
    API_MAIL_markMessage,
    API_MAIL_sendMessage,
} from '../constants/endpoints'
import { MailBoxType } from '../constants/enums'
import ParseEmoteOutput from '../libraries/mfc/ParseEmoteOutput'
import { MailPostResonse } from '../mfcLogic/api/mail'
import { authenticated } from '../mfcLogic/auth'
import { controller } from '../utilities/abortController'
import { isBrowser } from '../utilities/browser'
import { pubSubMail } from './pubSub'

const calcUnread = () => {
    const totalNew = MAILSTATE.mail.messages.inbox.filter(
        ({ message_read }) => message_read === 0,
    ).length
    MAILSTATE.mail.unread = totalNew
    emitMailboxUpdate(MAILSTATE)
}

export const emitMailboxUpdate = (mailState: MailState) => {
    // emit the new state to the subscriptions,
    // so the useMail hooks get them
    // - get a new ref so hooks work
    MAILSTATE.mail = { ...mailState.mail }
    pubSubMail.emit('mail', MAILSTATE.mail, true)
}

const buildMessage = (
    message: FetchMailMessage,
    box: Box,
    users: Record<number, FetchMailUser>,
) => {
    const messageUser =
        box === MailBoxType.inbox
            ? message.from_id
            : box === 'sent'
              ? message.to_id
              : undefined
    const user = users[messageUser]
    const { access_level = 0 } = user
    return {
        ...message,
        user_info: {
            ...user,
            model: access_level === 4,
            hasAvatar: access_level > 2,
        },
    }
}

type BuildMailPayload = {
    box: Box
    mail: FetchMailResponse
    reset?: boolean
}

export const buildMail = (mailPayload: BuildMailPayload) => {
    const { inbox = [], sent = [] } = MAILSTATE.mail.messages
    const {
        box,
        mail: { messages = [], users },
        reset,
    } = mailPayload
    // grab the existing messages
    const boxMessages =
        box === MailBoxType.inbox ? inbox : box === 'sent' ? sent : []

    // convert them to a map keyed by id
    const existingById = boxMessages.reduce(
        (messageMap, message) => {
            messageMap[message.id] = message
            return messageMap
        },
        {} as Record<number, IMailMessage>,
    )
    // a reset drops messages missing from the response, but fetched bodies
    // still carry over for the ones that remain
    const messageMap: Record<number, IMailMessage> = reset
        ? {}
        : { ...existingById }
    // update/add the new messages to the map
    messages.forEach((message) => {
        // merge these together, so we don't lose the body and anything else
        const existing = existingById[message.id]
        messageMap[message.id] = {
            ...existing,
            ...buildMessage(message, box, users),
        }
    })
    // convert map to array
    // and sort array by creation_date
    const messageList = Object.keys(messageMap)
        .map((id) => messageMap[Number(id)])
        .sort((a, b) => (a.creation_date < b.creation_date ? 1 : -1))

    MAILSTATE.mail.messages[box] = messageList
    MAILSTATE.mail.loading = false
    calcUnread()
    emitMailboxUpdate(MAILSTATE)
}

export const handleMailBody = (mailPayload: MailBody) => {
    const { inbox = [], sent = [] } = MAILSTATE.mail.messages

    const { box, mailId, body, parsedBody, error } = mailPayload
    // new objects so memoised consumers see the change
    const newBox = (box === MailBoxType.inbox ? inbox : sent).map((message) =>
        message.id === mailId
            ? { ...message, body, parsedBody, error }
            : message,
    )

    MAILSTATE.mail.messages[box] = newBox.sort((a, b) =>
        a.creation_date < b.creation_date ? 1 : -1,
    )
    emitMailboxUpdate(MAILSTATE)
}

type MarkMailPayload = {
    box: Box
    key: 'delete' | 'message_read' | 'star'
    mailIds: Array<number>
    value: 0 | 1
}

export const handleMailMark = (mailPayload: MarkMailPayload) => {
    const { mailIds = [], box, key, value } = mailPayload
    const mailIdsSet = new Set(mailIds)
    const mailbox = MAILSTATE.mail.messages[box]

    if (mailbox) {
        const markMessages = () => {
            if (key === 'delete') {
                return mailbox.filter((message) => !mailIdsSet.has(message.id))
            } else {
                return mailbox.map((message) =>
                    mailIdsSet.has(message.id)
                        ? { ...message, [key]: value }
                        : message,
                )
            }
        }
        const updatedMailbox = markMessages()
        MAILSTATE.mail.messages[box] = updatedMailbox.sort((a, b) =>
            a.creation_date < b.creation_date ? 1 : -1,
        )

        calcUnread()
        emitMailboxUpdate(MAILSTATE)
    }
}

export const openMailMessage = (mailPayload: MailboxPayload) => {
    const { box, mailId } = mailPayload

    MAILSTATE.mail.opened = {
        box,
        mailId,
    }

    emitMailboxUpdate(MAILSTATE)
}

export const setLoading = () => {
    MAILSTATE.mail.loading = true
    emitMailboxUpdate(MAILSTATE)
}

// these are our state modifying subscriptions
// (only used by useMail, so we don't need to subscribe to them on boot)
pubSubMail.on('*mail_response', buildMail)
pubSubMail.on('*mail_body', handleMailBody)
pubSubMail.on('*mail_opened', openMailMessage)
pubSubMail.on('*mail_mark', handleMailMark)

const emoteParser = new ParseEmoteOutput()

export const parseBodyImg = (body: string): string =>
    emoteParser.Parse(body, { darkMode: APPSTATE.app.darkMode })

export const _markMailMessage = async (
    ids: Array<number> = [],
    action: 'star' | 'read' | 'delete' | 'delete_sent',
    value: 0 | 1,
    token: string,
) => {
    // valid actions are: `star`, `read`, `delete`, `delete_sent`
    // set the value to 0 or 1 to toggle it
    const resp = await fetch(API_MAIL_markMessage, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ids: ids.join(','),
            key: action,
            value,
            token,
        }),
        signal: controller.signal,
    })
    return await resp.json()
}

type FetchMailBodyResponse = {
    body: string
    from_id: number
    id: number
    mode: 0 | 1
}

export const _fetchMailBody = async (id: number) => {
    const resp = await fetch(`${API_MAIL_getMessage}?id=${id}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        signal: controller.signal,
    })
    if (!resp.ok) throw new Error(`[getMessage] failed: ${resp.status}`)
    const json = (await resp.json()) as FetchMailBodyResponse
    const { body } = json
    if (typeof body !== 'string') throw new Error('[getMessage]: no body')
    // process the body to parse out image urls and short codes
    return [body, parseBodyImg(body)] as const
}

export const _postMail = async (
    to = '',
    subject = '',
    message = '',
    token: string,
) => {
    const response = await fetch(API_MAIL_sendMessage, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            recipient_usernames: to,
            subject,
            body: message,
            token,
        }),
        signal: controller.signal,
    })
    const json: MailPostResonse = await response.json()
    return json
}

type FetchMailMessage = {
    admin_message: 0 | 1
    creation_date: number
    from_id: number
    id: number
    message_read: 0 | 1
    mode: 0 | 1
    replied_to: 0 | 1
    star: 0 | 1
    subject: string
    to_id: number
    username: string
}

type FetchMailUser = {
    access_level: number
    chat_text_color?: string
    chat_text_font: number
    chat_text_font_flags: number
    friend: 0 | 1
    id: number
    style_string?: string
    user_font: string
    user_id: number
    username: string
}

type FetchMailResponse = {
    limit: number
    messages: Array<FetchMailMessage>
    offset: number
    options: {
        limit?: string
        name?: 'received' | 'sent'
        offset?: string
        selected_tab?: string
        sent_messages?: boolean
        sort_by?: string
        sort_descend?: number
        title?: string
    }
    total_messages: number
    unread: number
    users: Record<number, FetchMailUser>
}

const _fetchMail = async (
    box: Box = MailBoxType.inbox,
    offset = 0,
    limit = 100,
    searchQuery?: string,
) => {
    const search = searchQuery ? `&search=${searchQuery}` : ''
    const resp = await fetch(
        `${
            box === 'sent' ? API_MAIL_getSentMessages : API_MAIL_getMessages
        }?offset=${offset}&limit=${limit}&sort_by=creation_date&sort_descend=1${search}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            signal: controller.signal,
        },
    )
    return (await resp.json()) as FetchMailResponse
}

export const checkMail = async (
    box: Box = MailBoxType.inbox,
    limit = 100,
    offset = 0,
    search?: string,
) => {
    const currentMessageLength = MAILSTATE.mail.messages[box]?.length
    if (!isBrowser() || !authenticated()) return
    try {
        setLoading()
        const mailResponse = await _fetchMail(box, offset, limit, search)
        const responseCount = mailResponse.messages?.length
        const totalCount = mailResponse?.total_messages
        // if our current mail message length matches response' total count
        if (currentMessageLength === totalCount) {
            MAILSTATE.mail.loading = false
            pubSubMail.emit(
                '*mail_response',
                { mail: mailResponse, box, reset: false },
                true,
            )
            return
        }
        if (mailResponse && responseCount > 0) {
            const { offset: currentOffset, total_messages } = mailResponse
            const remainingMessages = total_messages - (responseCount + offset)
            const newOffset = currentOffset + responseCount
            // get the rest of the messages
            if (remainingMessages > 0) {
                checkMail(box, remainingMessages, newOffset, search)
            } else {
                MAILSTATE.mail.loading = false
            }
        }
        // we should emit what we got back, so it can be processed
        pubSubMail.emit(
            '*mail_response',
            { mail: mailResponse, box, reset: !!search },
            true,
        )
    } catch (_err) {
        // console.error(err)
    }
}
